import AlertMessage from 'components/AlertMessage';
import CommentDialog from 'components/CommentDialog';
import ConfirmDialog from 'components/ConfirmDialog';
import CTADrawer from 'components/ConfirmDrawer/CTADrawer';
import EventListener from 'components/Listener/EventListener';
import Login from 'components/Login';
import ExpireExtensionFormModal from 'components/Modal/ExpireExtensionFormModal';
import CloneToDraftModal from 'components/Modal/CloneToDraftModal';
import NavigationBar from 'components/NavigationBar';
import NavigationDrawer from 'components/NavigationDrawer';
import RootHeader from 'components/RootHeader';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import UploadCTAPopup from 'views/components/upload/uploadCTAPopup';
import ErrorModal from 'components/Modal/ErrorModal';
import GlobalLoading from 'components/GlobalLoading';
import AccountActivation from 'components/Login/accountActivation';
import { useHistory } from 'react-router';
import { useState, useEffect } from 'react';
import ResetPassword from 'components/Login/resetPassword';
import LoadingIndicator from 'components/LoadingIndicator';
import useDialog from 'components/ConfirmDialog/useDialog';
import { setGlobal } from 'store/global/reducer';
import ViewApprovalLetter from 'components/PostApproval/viewApprovalLetter';
import { useLocation } from 'react-router-dom';
import Help from 'components/Help';
import useQuery from 'hooks/useQuery';
import { getDialogContent } from 'utility/general';

const SContentLayout = styled.div`
    display: flex;
    flex-grow: 1;
    height: 100%;
    flex-direction: column;
`;

const SPageContent = styled.div`
    display: flex;
    position: relative;
    width: 100%;
    flex-grow: 1;
`;

const SMainLayout = styled.div`
    height: 100vh;
    min-width: ${(props) => props.minWidth}px;
    display: flex;
    flex-direction: column;
`;

const SHeaderFixed = styled.div`
    position: fixed;
    z-index: 1299;
    left: 0;
    right: 0;
    top: 0;
    min-width: 1000px;
`;

const SHeaderRelative = styled.div`
    position: relative;
    opacity: 0;
`;

const SHeaderLayout = styled.div``;

const SContentFlex = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    overflow: auto;
`;

const appMinWidth = 1280;

const GlobalModal = () => {
    const isShowViewApprovalLetter = useSelector((state) => state.global.isShowViewApprovalLetter);
    const isExtensionFormOpen = useSelector((state) => state.global.isExtensionFormOpen);
    const isCloneToDraftOpen = useSelector((state) => state.global.isCloneToDraftOpen);
    const openUploadCTAPopup = useSelector((state) => state.global.openUploadCTAPopup);
    const showCTAUploadDrawer = useSelector((state) => state.global.showCTAUploadDrawer);
    const isShowConfirmModal = useSelector((state) => state.global.isShowConfirmModal);
    const dispatch = useDispatch();
    const modalDescription = useSelector((state) => state.global.modalDescription);
    const showDialog = useDialog();

    useEffect(() => {
        if (isShowConfirmModal) {
            const confirmDialog = async () => {
                await showDialog({
                    description: modalDescription,
                    disableCancelButton: true,
                });
                dispatch(setGlobal({ isShowConfirmModal: false }));
            };

            confirmDialog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowConfirmModal]);

    return (
        <>
            <CTADrawer isShow={showCTAUploadDrawer} />
            <ConfirmDialog />
            <CommentDialog />
            <AlertMessage />
            <ErrorModal />
            <ExpireExtensionFormModal open={isExtensionFormOpen ?? false} />
            <CloneToDraftModal open={isCloneToDraftOpen ?? false} />
            {openUploadCTAPopup && <UploadCTAPopup />}
            <ViewApprovalLetter open={isShowViewApprovalLetter ?? false} />
        </>
    );
};

const MainLayout = ({ children }) => {
    const isLogin = useSelector((state) => state.user.isLogin);
    const isGlobalLoadingShow = useSelector((state) => state.global.isGlobalLoadingShow);
    const history = useHistory();
    const location = useLocation();
    const postDialog = useQuery('post-dialog');
    const [isAccountActivationUrl, setIsAccountActivationUrl] = useState(
        location.pathname === '/user/account-activation',
    );
    const [isResetPasswordUrl, setIsResetPasswordUrl] = useState(location.pathname === '/user/reset-password');

    const userMenu = useSelector((state) => state.menu);
    const dispatch = useDispatch();

    useEffect(() => {
        if (postDialog) {
            const { title, description } = getDialogContent(postDialog);

            dispatch(
                setGlobal({
                    isErrorShow: true,
                    errorTitle: title,
                    errorMessage: description,
                }),
            );

            history.push(location.pathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postDialog]);

    const getLayout = () => {
        if (location.pathname === '/guide' && !isLogin) {
            return <Help showManualOnly />;
        } else if (isAccountActivationUrl) {
            return <AccountActivation setIsAccountActivationUrl={setIsAccountActivationUrl} />;
        } else if (isResetPasswordUrl) {
            return <ResetPassword setIsResetPasswordUrl={setIsResetPasswordUrl} />;
        } else if (!isLogin) {
            return (
                <div style={{ overflow: 'auto', height: '100%' }}>
                    <Login />
                </div>
            );
        } else {
            if (userMenu.data.length === 0 || userMenu.isLoading) {
                return (
                    <div
                        style={{
                            width: '100vw',
                            height: '100vh',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <LoadingIndicator />
                    </div>
                );
            }

            return (
                <NavigationDrawer>
                    <SContentLayout>
                        <NavigationBar />
                        <SPageContent>
                            <SContentFlex>{children}</SContentFlex>
                        </SPageContent>
                    </SContentLayout>
                </NavigationDrawer>
            );
        }
    };

    if (postDialog) {
        return null;
    }

    return (
        <SMainLayout minWidth={appMinWidth}>
            <SHeaderLayout>
                <SHeaderFixed>
                    <RootHeader minWidth={appMinWidth} />
                </SHeaderFixed>
                <SHeaderRelative>
                    <RootHeader minWidth={appMinWidth} />
                </SHeaderRelative>
            </SHeaderLayout>
            {isGlobalLoadingShow && <GlobalLoading />}
            <GlobalModal />
            <EventListener />
            {getLayout()}
        </SMainLayout>
    );
};

export default MainLayout;
